import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { FieldState, FieldViewProps } from '../types';
import { fieldStateMap } from '../constants';
import { Init, Intro, Camera, Check, ImageValue } from '../components';
import { useFieldContext } from '../../../context';
import { isAndroid } from 'react-device-detect';

export function useFieldController(
  type: FieldViewProps['type'],
  allowGallery: FieldViewProps['allowGallery']
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const captureRef = useRef<HTMLInputElement>(null);
  const { value } = useFieldContext();
  const [currentState, setCurrentState] = useState<FieldState>(
    value?.id ? 'result' : 'init'
  );
  const [rawImage, setRawImage] = useState<{ file: Blob; url: string }>();

  useEffect(() => {
    if (value?.id) {
      setCurrentState('result');
    }
  }, [value]);

  const ComponentByState = useMemo(() => {
    if (currentState === fieldStateMap.init) {
      return Init;
    }
    if (currentState === fieldStateMap.intro) {
      return Intro;
    }
    if (currentState === fieldStateMap.camera) {
      return Camera;
    }
    if (currentState === fieldStateMap.check) {
      return Check;
    }
    if (currentState === fieldStateMap.result) {
      return ImageValue;
    }
    return Init;
  }, [currentState]);

  const onChangeImage = useCallback(
    (event) => {
      event.preventDefault();
      const file = event?.target?.files[0];
      if (file) {
        try {
          setRawImage({
            file,
            url: (window.URL || window.webkitURL).createObjectURL(file),
          });
          setCurrentState('check');
        } catch (e) {}
      }
    },
    [setRawImage, setCurrentState]
  );

  const contextData = useMemo(
    () => ({
      isFace: type === 'face',
      isDocument: type === 'document',
      setCurrentState,
      setRawImage,
      rawImage,
      inputRef,
      captureRef,
      allowGallery,
    }),
    [type, rawImage, allowGallery]
  );

  const isCapture = useMemo(() => {
    const input = document.createElement('input');
    return input.capture !== undefined && !isAndroid;
  }, []);

  return {
    contextData,
    inputRef,
    onChangeImage,
    ComponentByState,
    captureRef,
    isCapture,
  };
}
